import * as React from 'react';
import { CSSProperties } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import SelectorLabel from '@/UiBuilder/SelectorLabel/SelectorLabel';
import { OptionsGrid } from '@/UiBuilder/OptionsGrid/OptionsGrid';
import { Card } from '@/UiBuilder/Card/Card';
import { SelectOptionAction } from '@/ui-kit/components/SelectOptionAction/SelectOptionAction';

import { useBootstrapper } from '@/ui-kit/contexts/BootstrapperContext';
import { RalAttributeCfgUIElementState } from '@/stores/RalAttributeCfgUIElementState';
import { OptionsViewStateName } from '@/stores/OptionsConfiguratorUIStore';
import { Icon, Typography } from '@canvas-logic/clp-shared-ui';

import styles from './RalAttributeCfgUIElement.module.css';
import { AttributeCfgUIElement } from '@/UiBuilder/elements/AttributeCfgUIElement/AttributeCfgUIElement';
import { IAttributeTextData } from '@canvas-logic/clp-domain';
import classNames from 'classnames';

interface RalViewProps {
  state: RalAttributeCfgUIElementState;
  textData: IAttributeTextData;
}

export const RalAttributeCfgUIElement = observer(function ({ state, textData }: RalViewProps) {
  const { t: tTranslation } = useTranslation('translation');
  const isCustomColorSelected = !!state.color && state.color.custom;
  const { uiControl } = state.viewElement;
  const customColorControl = uiControl.customColorOptions;
  const bootstrapper = useBootstrapper();
  const cardOrientation = uiControl.layout === 'card' ? 'vertical' : 'horizontal';

  return (
    <AttributeCfgUIElement state={state} textData={textData}>
      <div>
        <ul
          className={classNames('cp-attribute__options', uiControl.layout === 'card' && 'cp-attribute__options--card')}
        >
          {state.availableColors.map((color) => {
            const isSelected = state.color?.value.ral === color.ral;

            return (
              <li key={color.ral} className={'cp-attribute__option'}>
                <Card
                  orientation={cardOrientation}
                  value={color.ral}
                  onSelect={state.setColor}
                  selected={isSelected}
                  label={uiControl.itemOptions?.showTitle ? color.ral : ''}
                  imageType="icon"
                  icon={uiControl.itemOptions?.showImage ? <ColorCell color={color.hex} /> : undefined}
                />
              </li>
            );
          })}
        </ul>
      </div>
      {state.hasCustom && (
        <div>
          <SelectorLabel
            className={styles.text}
            component="div"
            title={customColorControl?.showTitle ? customColorControl.title : ''}
            description={''}
          />
          <SelectOptionAction
            selected={isCustomColorSelected}
            onClick={() =>
              bootstrapper.bootstrapper.optionsViewStore.setView({
                name: OptionsViewStateName.SELECT_CUSTOM_COLOR,
                state,
              })
            }
          >
            {isCustomColorSelected ? (
              <div className={styles.actionStartElem}>
                {uiControl.customColorOptions?.showImage && (
                  <ColorCell width={80} height={80} color={state.color?.value.hex ?? ''} />
                )}
                {uiControl.customColorOptions?.showTitle && (
                  <Typography component="div" variant="button">
                    {state.color?.value.ral}
                  </Typography>
                )}
              </div>
            ) : (
              <div className={styles.actionStartElem}>
                <Icon type="palette" colored />
                <div>{uiControl.customColorOptions?.title || tTranslation('buttons.select')}</div>
              </div>
            )}
          </SelectOptionAction>
        </div>
      )}
    </AttributeCfgUIElement>
  );
});

export const ColorCell = ({ color, width, height }: { color: string; width?: number; height?: number }) => {
  const style = {
    '--color-cell-value': color,
    ...(width && { '--color-cell-width': `${width}px` }),
    ...(height && { '--color-cell-height': `${height}px` }),
  } as CSSProperties;

  return <div style={style} className={styles.colorCell} />;
};
